import { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { ErrorCode } from '@brimo/shared';
import { Skeleton } from '../../../ds/components';
import { ApiError } from '../../../lib/api';
import { usePortalMagic } from '../api';
import { PhoneFrame } from '../frame/PhoneFrame';
import styles from '../frame/Phone.module.css';
import { storePortalMagicToken, type MagicArrivalState } from '../magic';
import type { PortalMagicExpired } from '../types';
import { ExpiredScreen, InvalidScreen } from './ExpiredScreen';
import { IntroScreen } from './IntroScreen';
import { PasswordScreen } from './PasswordScreen';

/**
 * `/portal/m/:token` — the magic link decides the screen (`GET /api/public/portal/magic/:token`):
 * `self_disclosure` → Portal 02 (then the M4 form `/sd/<token>` = 03–07), `password` → 08 (→ 09),
 * `done` → the organization's login, 410 → 10, 404 → „Dieser Link ist ungültig." (own).
 */
export function MagicPage() {
  const { token = '' } = useParams();
  const location = useLocation();
  const fromSubmit = (location.state as MagicArrivalState | null)?.fromSubmit === true;
  const query = usePortalMagic(token);

  useEffect(() => {
    if (token) storePortalMagicToken(token);
  }, [token]);

  if (query.isPending || (query.isFetching && !query.isFetchedAfterMount)) {
    return (
      <PhoneFrame>
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width="30%" height={14} />
          <Skeleton width="80%" height={28} />
          <Skeleton height={120} />
        </div>
      </PhoneFrame>
    );
  }

  if (query.isError) {
    const error = query.error;
    if (error instanceof ApiError && error.status === 410 && error.code === ErrorCode.LINK_EXPIRED) {
      return <ExpiredScreen token={token} expired={(error.details ?? {}) as unknown as PortalMagicExpired} />;
    }
    return <InvalidScreen notFound={error instanceof ApiError && error.status === 404} />;
  }

  const view = query.data;
  if (view.state === 'done') return <Navigate to={`/portal/${view.organization.slug}/login`} replace />;
  if (view.state === 'self_disclosure') return <IntroScreen view={view} />;
  return <PasswordScreen token={token} view={view} fromSubmit={fromSubmit} />;
}
